import type { CurrentSeasonArea, FieldSeason } from '@protector/entities/property/models';
import type { FieldsBySeasonsSyncResponse, FieldWithCropzone } from '@protector/entities/region/field-cropzone.models';
import { getFieldsWithCropzones, getFieldsWithCropzonesSync } from '@protector/entities/region/field-cropzone.services';
import { adaptFieldsWithCropzones } from '@protector/entities/region/field-cropzone.utils';
import type {
  AreaVariable,
  CurrentInfo,
  Field,
  IPayloadRegionData,
  IRegionDataResponse,
  Region,
  UpdateSeasonAreasMassivelyPayload,
  V1Region
} from '@protector/entities/region/models';
import { SeasonQueryKey } from '@protector/entities/season/constants';
import type { Season } from '@protector/entities/season/models';
import type { Nullable, UUID } from '@protector/utils/types';
import axios from 'axios-observable';
import { PROTECTOR_API_URL } from 'config/constants';
import { queryClient } from 'config/react-query';
import { captureError } from 'infra/monitoring/monitoring.utils';
import _ from 'lodash';
import type { Observable } from 'rxjs';
import { forkJoin, from, of } from 'rxjs';
import { fromPromise } from 'rxjs/internal-compatibility';
import { catchError, concatMap, exhaustMap, map } from 'rxjs/operators';
import { FIELDS_STORE_NAME, IndexedDBPromiseAPI, PROPERTY_ID_INDEX } from '../../core/indexeddb.utils';
import { getObservables } from '../../core/rxjs-utils';
import { getSeasonFieldFromPropertyBySeasonIdsObservable } from '../../querys/season-field/season-field.service';
import type { FieldIdsByEndDateInSeasonField } from '../field/field.service';
import { getAllFieldsByIds, mapSeasonFieldToFieldSeasonByFieldId } from '../field/field.service';
import { getRegionsByProperty, getSeasonAreasBySeasons } from '../property/property.service';
import type { StoredFieldWithCropzone, StoredSyncWatermark } from './region.fields-cropzone-cache';
import {
  applyFieldsCropzoneSync,
  clearFieldsCropzoneCacheForProperty,
  computeFieldsCropzoneCoverage,
  getCachedFieldsForProperty,
  getSyncWatermarksForProperty,
  NO_CROPZONE_SEASON_ID,
  primeFieldsCropzoneCache,
  recordSyncWatermark,
  selectActiveFieldPerId
} from './region.fields-cropzone-cache';
import { buildDeepRegion, getFieldIdsByEndDateInSeasonField } from './region.utils';

const protectorApiUrl = PROTECTOR_API_URL;
const archeTimelineUrl = `${protectorApiUrl}/v1/timeline`;
const regionUrl = `${protectorApiUrl}/v1/panel/property/`;
const areaVariableUrl = `${protectorApiUrl}/v1/area-variables`;
const CHUNK_CURRENT_INFO_FIELD_IDS = 300;
const regionDataURL = (id: string) => `${regionUrl}${id}/regions/last-event`;
/**
 * The `/sync` watermark is captured from the client clock, not a server-issued value, so a
 * forward-skewed device clock could otherwise persist a watermark past server mutations that
 * land inside the skew window, permanently dropping them from future `/sync` deltas. Subtracting
 * a safety margin keeps the persisted watermark behind the true request time; the resulting
 * over-fetch is cheap since the cache writes it drives are idempotent `put`s.
 */
const CLOCK_SKEW_SAFETY_MARGIN_MS = 5 * 60 * 1000;

/** Tracks which properties already had their fields-by-seasons cache wiped this session (see `getRegionForFieldSeasons`). */
const clearedFieldsCropzoneCacheForProperty = new Set<UUID>();

export interface FieldCurrentInfoResource {
  field_id: UUID;
  current_info: CurrentInfo;
}

export interface GetRegionOptions {
  useSeasonAreasFromActiveSeasons?: boolean;
  getGeometriesBySeasonFieldEndDate?: boolean;
  includeArchived?: boolean;
  propertyTimeZone?: string;
  useFieldsBySeasonsEndpoint?: boolean;
  useFieldsBySeasonsCache?: boolean;
}

/**
 * Get Region tree. The region tree is composed by its children and its information like name, id, seasons, crops - for each subregion too.
 * @param propertyId {UUID} Property Id.
 * @param seasons {Season[]} Related seasons for load only fields from seasons.
 * @param geometryDate Used for field versioning
 * @param seasonAreasFromActiveSeasons Current season areas for dont need to load season fields again
 * @param options.useSeasonAreasFromActiveSeasons If is for use season areas for load regions
 * @param options.getGeometriesBySeasonFieldEndDate If true, fetches geometries using season field end dates
 * @param options.propertyTimeZone Property timezone used to resolve season end dates into geometry reference dates
 * @param options.useFieldsBySeasonsEndpoint If true, uses the new consolidated fields-by-seasons endpoint (see GetRegionOptions)
 */
export const getRegion = (
  propertyId: UUID,
  seasons: Season[],
  geometryDate: Nullable<string> = null,
  seasonAreasFromActiveSeasons: CurrentSeasonArea[] = [],
  options: GetRegionOptions = {}
): Observable<Region> => {
  const {
    useSeasonAreasFromActiveSeasons = false,
    getGeometriesBySeasonFieldEndDate = false,
    includeArchived = false,
    propertyTimeZone,
    useFieldsBySeasonsEndpoint = false,
    useFieldsBySeasonsCache = true
  } = options;
  const fieldSeasonsOptions: GetRegionForFieldSeasonsOptions = {
    getGeometriesBySeasonFieldEndDate,
    includeArchived,
    propertyTimeZone,
    useFieldsBySeasonsEndpoint,
    useFieldsBySeasonsCache
  };
  return useSeasonAreasFromActiveSeasons
    ? getRegionForFieldSeasons(propertyId, seasons, geometryDate, seasonAreasFromActiveSeasons, fieldSeasonsOptions)
    : getRegionForSeason(propertyId, seasons, geometryDate, fieldSeasonsOptions);
};

export const getArchivedSeasonFields = (propertyId: UUID, seasonIds: UUID[]): Observable<FieldSeason[]> => {
  return getSeasonFieldFromPropertyBySeasonIdsObservable({ propertyIds: [propertyId], seasonIds, update: true }).pipe(
    map(seasonFields => seasonFields.map(mapSeasonFieldToFieldSeasonByFieldId))
  );
};

/**
 * Get Regions tree for some season. Just fields in the seasons will be returned here.
 * @param regionId {UUID} Root region id.
 * @param propertyId {UUID} Related property for get fields.
 * @param seasons {Season[]>} Related seasons for get fields.
 * @param geometryDate Used for field versioning
 */
const getRegionForSeason = (
  propertyId: UUID,
  seasons: Season[],
  geometryDate: Nullable<string> = null,
  options: GetRegionForFieldSeasonsOptions = {}
): Observable<Region> => {
  const seasonIds = seasons.map(s => s.id);

  if (options.useFieldsBySeasonsEndpoint) {
    return getRegionForFieldSeasons(propertyId, seasons, geometryDate, [], options);
  }

  return forkJoin([getSeasonAreasBySeasons(propertyId, seasonIds)]).pipe(
    exhaustMap(([seasonsFieldsFlat]) => {
      queryClient.setQueryData([SeasonQueryKey.GET_ALL_SEASON_AREAS_BY_SEASONS, seasonIds, propertyId], seasonsFieldsFlat);
      const seasonFields = seasonsFieldsFlat.flat(Infinity);
      return getRegionForFieldSeasons(propertyId, seasons, geometryDate, seasonFields, options);
    })
  );
};

const mergeFieldSeasonsByAreaId = (base: FieldSeason[], extra: FieldSeason[]): FieldSeason[] => {
  const byAreaId = new Map<UUID, FieldSeason>();
  [...base, ...extra].forEach(seasonField => {
    if (!byAreaId.has(seasonField.areaId)) {
      byAreaId.set(seasonField.areaId, seasonField);
    }
  });
  return [...byAreaId.values()];
};

/**
 * Get Regions tree for current season areas. Just active areas will be returned here.
 * @param propertyId {UUID} Related property for get fields.
 * @param seasons {Season[]>} Related seasons for get fields.
 * @param geometryDate Used for field versioning
 * @param fieldSeasons Current season areas loaded
 */
type FieldValidity = Pick<Field, 'valid_since' | 'valid_until'>;

/**
 * Fetches each field's current validity window (reference_date = today) so archive
 * status reflects the field's present state rather than the season-end-pinned
 * historical shape used for geometry. Without this, an unarchived field whose crop
 * cycle already ended keeps the stale valid_until from its pre-unarchive shape and
 * stays flagged archived on the timeline (P40-60987).
 */
const getCurrentFieldValidityById = (fieldIds: UUID[], includeArchived: boolean): Observable<Record<string, FieldValidity>> => {
  if (!fieldIds.length) {
    return of({});
  }
  return getAllFieldsByIds(fieldIds, true, null, false, undefined, includeArchived).pipe(
    map(fieldPages =>
      fieldPages.flat().reduce<Record<string, FieldValidity>>((accumulator, field) => {
        accumulator[field.id] = { valid_since: field.valid_since, valid_until: field.valid_until };
        return accumulator;
      }, {})
    )
  );
};

const fillMissingFieldSeasons = (fields: Field[], fieldSeasons: FieldSeason[]): FieldSeason[] => {
  const areaIdsWithSeason = new Set(fieldSeasons.map(fieldSeason => fieldSeason.areaId));
  const placeholders = fields
    .filter(field => !areaIdsWithSeason.has(field.id))
    .map(field => ({ id: field.id, areaId: field.id, seasonId: NO_CROPZONE_SEASON_ID, varieties: [] }));
  return [...fieldSeasons, ...placeholders];
};

interface GetRegionForFieldSeasonsOptions {
  getGeometriesBySeasonFieldEndDate?: boolean;
  includeArchived?: boolean;
  propertyTimeZone?: string;
  useFieldsBySeasonsEndpoint?: boolean;
  useFieldsBySeasonsCache?: boolean;
}

/** Shared by every fields-by-seasons path (cache-bypassed or not) so their region-building step can't drift apart. */
const buildRegionFromFields = (
  abstractRegions: V1Region[],
  fields: FieldWithCropzone[],
  seasons: Season[],
  propertyTimeZone?: string
): Region => {
  const { fields: adaptedFields, fieldSeasons: adaptedFieldSeasons } = adaptFieldsWithCropzones(
    selectActiveFieldPerId(fields, propertyTimeZone)
  );
  return buildDeepRegion(abstractRegions, fillMissingFieldSeasons(adaptedFields, adaptedFieldSeasons), adaptedFields, seasons);
};

const getRegionForFieldSeasons = (
  propertyId: UUID,
  seasons: Season[],
  geometryDate: Nullable<string>,
  fieldSeasons: FieldSeason[],
  options: GetRegionForFieldSeasonsOptions = {}
): Observable<Region> => {
  const {
    getGeometriesBySeasonFieldEndDate = false,
    includeArchived = false,
    propertyTimeZone,
    useFieldsBySeasonsEndpoint = false,
    useFieldsBySeasonsCache = true
  } = options;
  const seasonIds = seasons.map(s => s.id);

  if (useFieldsBySeasonsEndpoint && !useFieldsBySeasonsCache) {
    /**
     * Bypasses the local cache entirely instead of just skipping the `/sync` decision: also wipes
     * this property's existing cached rows and watermarks, so turning the cache back on later
     * doesn't resume from data that went stale while it was off. Only kicked off once per property
     * per session (`clearedFieldsCropzoneCacheForProperty`) — every region load for that property
     * while the flag is off would otherwise re-trigger the same IndexedDB reads/deletes for nothing,
     * since there's nothing left to clear after the first pass. Marked cleared only once the wipe
     * actually resolves, not before, so a rejected wipe is retried on the next load instead of being
     * silently treated as done.
     */
    if (!clearedFieldsCropzoneCacheForProperty.has(propertyId)) {
      clearFieldsCropzoneCacheForProperty(propertyId)
        .then(() => clearedFieldsCropzoneCacheForProperty.add(propertyId))
        .catch(error => captureError(error, { errorType: 'IndexedDBError', domain: 'Region' }));
    }

    return forkJoin([
      getRegionsByProperty(propertyId),
      from(getFieldsWithCropzones({ propertyId, seasonIds, includeGeometry: true, includeArchived }))
    ]).pipe(
      map(([abstractRegions, fieldsWithCropzonesResponse]) =>
        buildRegionFromFields(abstractRegions, fieldsWithCropzonesResponse.data.content, seasons, propertyTimeZone)
      )
    );
  }

  if (useFieldsBySeasonsEndpoint) {
    /**
     * The cache may now hold rows written during this on-window, so if the flag flips off again
     * later that property must go through the wipe above once more instead of being skipped as
     * "already cleared" from an earlier off-window.
     */
    clearedFieldsCropzoneCacheForProperty.delete(propertyId);
    /**
     * Read both stores behind a single `catchError` so they can never disagree: if either read
     * rejects, both fall back to empty together, instead of one read failing while the other
     * succeeds and produces a `/sync` decision based on a cache that doesn't match the watermark.
     */
    const localCache$ = from(
      Promise.all([getCachedFieldsForProperty(propertyId), getSyncWatermarksForProperty(propertyId)]).then(
        ([cachedFields, syncWatermarks]) => ({ cachedFields, syncWatermarks })
      )
    ).pipe(
      catchError(error => {
        captureError(error, { errorType: 'IndexedDBError', domain: 'Region' });
        return of({ cachedFields: [] as StoredFieldWithCropzone[], syncWatermarks: [] as StoredSyncWatermark[] });
      })
    );

    return forkJoin([getRegionsByProperty(propertyId), localCache$]).pipe(
      exhaustMap(([abstractRegions, { cachedFields, syncWatermarks }]) => {
        const coverage = computeFieldsCropzoneCoverage(syncWatermarks, cachedFields, seasonIds);
        const updatedSince = coverage?.lastSyncedAt;
        const requestedAt = new Date(Date.now() - CLOCK_SKEW_SAFETY_MARGIN_MS).toISOString();

        const fieldsWithCropzones$ = updatedSince
          ? from(
              getFieldsWithCropzonesSync({
                propertyId,
                seasonIds,
                updatedSince,
                includeGeometry: true,
                includeArchived
              })
            )
          : from(getFieldsWithCropzones({ propertyId, seasonIds, includeGeometry: true, includeArchived }));

        return fieldsWithCropzones$.pipe(
          map(fieldsWithCropzonesResponse => {
            const { content } = fieldsWithCropzonesResponse.data;
            const { fields: allFields, persisted } = updatedSince
              ? applyFieldsCropzoneSync(
                  seasonIds,
                  content,
                  (fieldsWithCropzonesResponse.data as FieldsBySeasonsSyncResponse).field_ids,
                  cachedFields
                )
              : primeFieldsCropzoneCache(seasonIds, content, cachedFields);

            /**
             * Only record the watermark once the field rows are actually persisted, so a failed
             * write can't leave a `/sync`-eligible watermark pointing at incomplete local data.
             * Also skip it entirely for a full fetch that returned zero fields: `persistFields`
             * resolves immediately without writing anything in that case, so recording a watermark
             * anyway would make a later `/sync` trust an empty local cache for this season.
             */
            const primeReturnedNoFields = !updatedSince && !content.length;
            if (!primeReturnedNoFields) {
              persisted.then(() => recordSyncWatermark(propertyId, seasonIds, requestedAt)).catch(() => void 0);
            }

            return buildRegionFromFields(abstractRegions, allFields, seasons, propertyTimeZone);
          })
        );
      })
    );
  }

  const archivedSeasonFields$ = includeArchived ? getArchivedSeasonFields(propertyId, seasonIds) : of([] as FieldSeason[]);

  return archivedSeasonFields$.pipe(
    exhaustMap(archivedSeasonFields => {
      const mergedSeasonFields = mergeFieldSeasonsByAreaId(fieldSeasons, archivedSeasonFields);
      const fieldsIds: UUID[] = mergedSeasonFields.map(sf => sf.areaId);

      let fieldIdsByEndDateInSeasonField;
      if (getGeometriesBySeasonFieldEndDate) {
        fieldIdsByEndDateInSeasonField = getFieldIdsByEndDateInSeasonField(mergedSeasonFields, propertyTimeZone);
      }

      const currentFieldValidityById$ = getGeometriesBySeasonFieldEndDate
        ? getCurrentFieldValidityById(fieldsIds, includeArchived)
        : of({} as Record<string, FieldValidity>);

      return forkJoin([
        getRegionsByProperty(propertyId),
        getFields(propertyId, fieldsIds, geometryDate, fieldIdsByEndDateInSeasonField, includeArchived),
        currentFieldValidityById$
      ]).pipe(
        exhaustMap(([abstractRegions, fields, currentFieldValidityById]) => {
          const formattedFields = fields.map(field => {
            const fieldWithGeometry = { ...field, geometry: field.geometry || { type: 'Polygon', coordinates: [] } };
            const currentValidity = currentFieldValidityById[field.id];
            return currentValidity
              ? { ...fieldWithGeometry, valid_since: currentValidity.valid_since, valid_until: currentValidity.valid_until }
              : fieldWithGeometry;
          });

          const deepRegion: Region = buildDeepRegion(abstractRegions, mergedSeasonFields, formattedFields, seasons);
          return of(deepRegion);
        })
      );
    })
  );
};

const getFields = (
  propertyId: UUID,
  fieldIds: UUID[],
  geometryDate: Nullable<string> = null,
  fieldIdsByEndDateInSeasonField?: FieldIdsByEndDateInSeasonField,
  includeArchived = false
): Observable<Field[]> => {
  return forkJoin([
    getAllFieldsByIds(fieldIds, true, geometryDate, false, fieldIdsByEndDateInSeasonField, includeArchived),
    getGeometriesCache(propertyId)
  ]).pipe(
    exhaustMap(([fields, cachedFields]) => {
      const idsNotCached = getFieldsNotCached(fields.flat(), cachedFields);

      const groupCachedFields = _.keyBy(cachedFields, 'id');

      const validCachedFields = fieldIds.reduce((accumulator, fieldId) => {
        if (!groupCachedFields[fieldId] || idsNotCached.includes(fieldId)) {
          return accumulator;
        }
        accumulator.push(groupCachedFields[fieldId]);
        return accumulator;
      }, [] as Field[]);

      let idsNotCachedByEndDateInSeasonField;
      if (fieldIdsByEndDateInSeasonField) {
        const endDates = Object.keys(fieldIdsByEndDateInSeasonField);
        idsNotCachedByEndDateInSeasonField = idsNotCached.reduce<Record<string, string[]>>((accumulator, fieldId) => {
          const mutableAccumulator = accumulator;

          endDates.forEach(endDate => {
            if (fieldIdsByEndDateInSeasonField[endDate].includes(fieldId)) {
              if (!mutableAccumulator[endDate]) {
                mutableAccumulator[endDate] = [];
              }

              mutableAccumulator[endDate].push(fieldId);
            }
          });

          return mutableAccumulator;
        }, {});
      }

      return forkJoin([
        of(validCachedFields),
        getGeometries(idsNotCached, true, geometryDate, idsNotCachedByEndDateInSeasonField, includeArchived)
      ]);
    }),
    exhaustMap(([cachedFields, requestFields]) => {
      const fields = [...cachedFields, ...requestFields.flat()];
      insertGeometriesCache(fields).catch(e => {
        console.log('error in Geometries Cache =>', e);
      });
      return of(fields);
    })
  );
};

const getFieldsNotCached = (fields: Field[], cachedFields: Field[]) => {
  const cachedFieldsById = _.mapKeys(cachedFields || [], 'id');
  return fields.reduce((accumulator, field) => {
    const updatedAtChanged = cachedFieldsById[field.id]?.updated_at !== field.updated_at;
    const validSinceChanged = cachedFieldsById[field.id]?.valid_since !== field.valid_since;
    if (updatedAtChanged || validSinceChanged) {
      accumulator.push(field.id);
    }

    return accumulator;
  }, [] as string[]);
};

const getGeometries = (
  ids: string[],
  useAsync: boolean,
  geometryDate: Nullable<string>,
  fieldIdsByEndDateInSeasonField?: FieldIdsByEndDateInSeasonField,
  includeArchived = false
) => {
  return ids?.length ? getAllFieldsByIds(ids, useAsync, geometryDate, true, fieldIdsByEndDateInSeasonField, includeArchived) : of([]);
};

const insertGeometriesCache = (fields: Field[]) => {
  return IndexedDBPromiseAPI.insertValues(FIELDS_STORE_NAME, fields).then(
    () => console.info('Geometries updated on IDB!'),
    error => captureError(error, { errorType: 'IndexedDBError', domain: 'Region' })
  );
};

const getGeometriesCache = (propertyId: string): Observable<Field[]> => {
  return fromPromise(IndexedDBPromiseAPI.queryIndex(FIELDS_STORE_NAME, PROPERTY_ID_INDEX, propertyId) as Promise<Field[]>).pipe(
    concatMap(result => of(result)),
    catchError(() => of([]))
  );
};

export const editAreaVariablesMassively = (areaVariables: AreaVariable[], propertyId: UUID) => {
  const url = `${protectorApiUrl}/v1/properties/${propertyId}/area-variables/update`;
  const requests = _.chunk(areaVariables, 500).map(chunk => {
    return axios.put(url, chunk);
  });
  return getObservables(requests, true).pipe(map(response => response.flat(1)));
};

export const createAreaVariablesMassively = (areaVariables: AreaVariable[], propertyId: UUID) => {
  const url = `${protectorApiUrl}/v1/properties/${propertyId}/area-variables/save`;
  const requests = _.chunk(areaVariables, 500).map(chunk => {
    return axios.post(url, chunk);
  });
  return getObservables(requests, true).pipe(map(response => response.flat(1)));
};

/**
 * @deprecated v1/season-areas will be deprecated in the future if possible use PUT /v2/season-fields/{uuid} */
export const updateSeasonAreasMassively = (payload: UpdateSeasonAreasMassivelyPayload) => {
  const url = `${protectorApiUrl}/v1/season-areas/update`;
  return axios.put(url, payload);
};

export const getPropertyCurrentInfo = (propertyId: UUID, seasonIds: UUID[]) => {
  return axios
    .post<Record<string, CurrentInfo>>(`${archeTimelineUrl}/${propertyId}/current-info`, {
      property_id: propertyId,
      season_ids: seasonIds
    })
    .pipe(map(response => response.data));
};

export const getCurrentInfosByFields = (propertyId: UUID, fieldIds: UUID[], seasonIds: UUID[]) => {
  return axios
    .post<FieldCurrentInfoResource[]>(`${regionUrl}${propertyId}/fields/current-info?allowPastSeasons=true`, {
      field_ids: fieldIds,
      season_ids: seasonIds
    })
    .pipe(map(response => response.data));
};

export const getCurrentInfosByFieldIdsPaginated = (propertyId: UUID, fieldIds: UUID[], seasonIds: UUID[]) => {
  const totalChunkedPages = Math.floor(fieldIds.length / CHUNK_CURRENT_INFO_FIELD_IDS + 1);
  const pages = Array.from({ length: totalChunkedPages }, (_, index) => index);
  const observables = pages.map(page => {
    const idsChunked = fieldIds.slice(page * CHUNK_CURRENT_INFO_FIELD_IDS, (page + 1) * CHUNK_CURRENT_INFO_FIELD_IDS);
    return getCurrentInfosByFields(propertyId, idsChunked, seasonIds);
  });
  return getObservables(observables, true);
};

export const getAreaVariable = (seasonId: UUID) => {
  return axios.get<AreaVariable[]>(`${areaVariableUrl}?season_id=${seasonId}&size=999`).pipe(map(response => response.data));
};

export const postAreaVariable = (propertyId: string | null, content: AreaVariable) => {
  return axios.post<unknown>(`${areaVariableUrl}/${propertyId}/save`, content).pipe(map(response => response.data));
};

export const updateAreaVariable = (propertyId: string | null, content: AreaVariable) => {
  return axios.put<unknown>(`${areaVariableUrl}/${propertyId}/update`, content).pipe(map(response => response.data));
};

export const deleteAreaVariable = (areaVariables: AreaVariable[], companyId?: string, propertyId?: string) => {
  return axios
    .delete<unknown>(`${areaVariableUrl}/delete`, {
      data: areaVariables.map(areaVariable => ({ ...areaVariable, company_id: companyId, property_id: propertyId }))
    })
    .pipe(map(response => response.data));
};

export const getRegionData = (payload: IPayloadRegionData) => {
  return axios
    .post<IRegionDataResponse[]>(`${regionDataURL(payload.propertyId)}`, { regionsIds: payload.regionsId })
    .pipe(map(response => response.data));
};
